import { Popup } from "devextreme-react";
import { Button } from "devextreme-react/button";
import { DateBox } from "devextreme-react/date-box";
import { memo, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AvailableEmployee2, isIndefiniteDate } from "../types";

type Props = {
  visible: boolean;
  isMobile: boolean;
  target: AvailableEmployee2 | null;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (payload: { startDate: Date; endDate: Date }) => Promise<void>;
};

export const AcceptEmployeePopup2 = memo(function AcceptEmployeePopup2({
  visible,
  isMobile,
  target,
  submitting,
  onClose,
  onSubmit,
}: Props) {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [error, setError] = useState<string | null>(null);

  const releaseStart = useMemo(
    () => (target ? new Date(target.releaseStartDate) : null),
    [target],
  );
  const releaseEnd = useMemo(() => {
    if (!target) return null;
    if (isIndefiniteDate(target.releaseEndDate)) return null;
    return new Date(target.releaseEndDate);
  }, [target]);

  useEffect(() => {
    if (visible && target) {
      const rs = new Date(target.releaseStartDate);
      const now = new Date();
      const start = rs > now ? rs : now;
      const end = releaseEnd ?? new Date(start.getTime() + 7 * 86400000);
      setStartDate(start);
      setEndDate(end);
      setError(null);
    }
  }, [visible, target, releaseEnd]);

  const handleSubmit = async () => {
    if (!target) return;
    if (endDate < startDate) {
      setError(t("transferOfEmployees2.endBeforeStart"));
      return;
    }
    if (releaseStart && startDate < new Date(releaseStart.toDateString())) {
      setError(t("transferOfEmployees2.outsideReleaseRange"));
      return;
    }
    if (releaseEnd && endDate > releaseEnd) {
      setError(t("transferOfEmployees2.outsideReleaseRange"));
      return;
    }
    setError(null);
    await onSubmit({ startDate, endDate });
  };

  const rangeInfo = useMemo(() => {
    if (!target) return "";
    const s = releaseStart ? releaseStart.toLocaleDateString() : "—";
    const e = releaseEnd
      ? releaseEnd.toLocaleDateString()
      : t("transferOfEmployees2.indefinite");
    return t("transferOfEmployees2.availableRange", { from: s, to: e });
  }, [target, releaseStart, releaseEnd, t]);

  return (
    <Popup
      visible={visible}
      onHiding={onClose}
      fullScreen={isMobile}
      maxWidth={isMobile ? undefined : 520}
      height={isMobile ? "100%" : "auto"}
      maxHeight={isMobile ? undefined : "85vh"}
      title={t("transferOfEmployees2.acceptTitle")}
      showCloseButton
      dragEnabled={!isMobile}
    >
      <div className="transfer2-form">
        <p className="transfer2-form-hint">
          {t("transferOfEmployees2.acceptHint")}
        </p>
        {target && (
          <div className="transfer2-form-target">
            <div className="transfer2-form-target-label">
              {t("transferOfEmployees2.employee")}
            </div>
            <div className="transfer2-form-target-value">
              {target.surname} {target.name}
            </div>
            <div className="transfer2-form-target-meta">
              {target.companyName}
              {target.position ? ` · ${target.position}` : ""}
            </div>
            <div className="transfer2-form-range">{rangeInfo}</div>
          </div>
        )}
        <div className="transfer2-form-row">
          <label className="transfer2-form-label" htmlFor="accept-start">
            {t("transferOfEmployees2.rentalStart")}
          </label>
          <DateBox
            id="accept-start"
            value={startDate}
            type="datetime"
            min={releaseStart ?? undefined}
            max={releaseEnd ?? undefined}
            onValueChanged={(e) => e.value && setStartDate(new Date(e.value))}
            width="100%"
          />
        </div>
        <div className="transfer2-form-row">
          <label className="transfer2-form-label" htmlFor="accept-end">
            {t("transferOfEmployees2.rentalEnd")}
          </label>
          <DateBox
            id="accept-end"
            value={endDate}
            type="datetime"
            min={startDate}
            max={releaseEnd ?? undefined}
            onValueChanged={(e) => e.value && setEndDate(new Date(e.value))}
            width="100%"
          />
        </div>
        {error && <div className="transfer2-form-error">{error}</div>}
        <div className="transfer2-form-actions">
          <Button
            text={t("common.cancel")}
            onClick={onClose}
            stylingMode="outlined"
            width={isMobile ? "100%" : undefined}
            disabled={submitting}
          />
          <Button
            text={t("transferOfEmployees2.acceptConfirm")}
            type="default"
            stylingMode="contained"
            onClick={handleSubmit}
            width={isMobile ? "100%" : undefined}
            disabled={submitting}
          />
        </div>
      </div>
    </Popup>
  );
});
